import React, { useMemo, useState, useEffect, useRef } from 'react'
import { View, Animated } from 'react-native'
// import MapView, { PROVIDER_GOOGLE, Marker } from 'react-native-maps'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Wrapper } from '@googlemaps/react-wrapper'
import { createCustomEqual } from 'fast-equals'
import { getThemeImages } from '@vfgroup-oneplatform/framework/Themes'

import PropTypes from 'prop-types'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'
import { ErrorScreen } from '@vfgroup-oneplatform/foundation/Components'

import CurrentLocationIcon from '../../Components/CurrentLocationIcon'
import StoresCardsList from '../../Components/StoresCardsList'
// import StoresMarkers from '../../Components/StoresMarkers/StoresMarkers'

import { getMapStyle, getCurrentLocation } from './helpers'
import styles from './StoreStep.styles'
import locationImage from '../../Images/Icons/Icons/icLocationRed.png'
import locationNavigator from '../../Images/Icons/Icons/location.png'


const DELTA = {
  latitudeDelta: 0.00922 * (1.5 / 3),
  longitudeDelta: 0.00922 * (1.5 / 3),
}

const Map = ({
  children,
  style,
  ...options
}) => {
  const ref = React.useRef(null)
  const [map, setMap] = React.useState()

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {})); 0
    }
  }, [ref, map])

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options)
    }
  }, [map, options])

  React.useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      )

    }
  }, [map])

  return (
    <>
      <div ref={ref} style={style} />

      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map })
        }
      })}
    </>
  )
}

const Marker = (options) => {
  const [marker, setMarker] = React.useState()
  const theme = useTheme()
  let icon = '/storeMarkerDark.png'

  if (theme.name === 'light') {
    icon = '/storeMarker.png'
  }
  if (options.isUserLocation) {
    icon = locationImage
  }

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker({
        icon: icon
      }))
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null)
      }
    }
  }, [marker])

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options)
    }
  }, [marker, options])

  return null
}

const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a, b) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b))
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b)
  }
)

function useDeepCompareMemoize(value) {
  const ref = React.useRef()

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value
  }

  return ref.current
}

function useDeepCompareEffectForMaps(
  callback,
  dependencies
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize))
}

const StoreStep = ({
  stores = [],
  onShowTimeSlotsPress,
  isLoading,
  initialSelectedStoreIndex,
  storesError,
  getStoresData,
  getStoresOpenHours
}) => {

  const mapRef = useRef()
  const theme = useTheme()
  const insets = useSafeAreaInsets()

  const mapThemes = useMemo(() => getMapStyle(theme.name), [theme.name])
  const [userCurrentLocation, setUserCurrentLocation] = useState(null)
  const [selectedStoreIndex, setSelectedStoreIndex] = useState(initialSelectedStoreIndex === -1 ? 0 : initialSelectedStoreIndex)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const mapOverlayOpacityAnimation = useRef(new Animated.Value(1))
  const [currentLocationDisabled, setCurrentLocationDisabled] = useState(false)
  const initialRegion = {
    latitude: stores?.[selectedStoreIndex]?.address?.latitude || 41.283105677319035,
    longitude: stores?.[selectedStoreIndex]?.address?.longitude || 19.8568675032758,
    ...DELTA,
  }
  const handleAnimateMapToSelectedStore = () => {
    const latitude = stores?.[selectedStoreIndex]?.address?.latitude || 41.283105677319035
    const longitude = stores?.[selectedStoreIndex]?.address?.longitude || 19.8568675032758
    mapRef.current?.animateToRegion({
      latitude,
      longitude,
      ...DELTA,
    })
    setCenter({
      lat: latitude,
      lng: longitude,
    })
  }

  const getUserCurrentLocation = () => {
    setCurrentLocationDisabled(null)
    getCurrentLocation(loc => {
      setUserCurrentLocation(loc)
    }, (err) => {
      setCurrentLocationDisabled(err)
    })
  }

  const handleNavigateToUserCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      setUserCoords({
        lat: coords.latitude,
        lng: coords.longitude
      })
      setCenter({
        lat: coords.latitude,
        lng: coords.longitude
      })
    }, null, { timeout: 10000 })
  }

  const onMapReady = () => {
    setIsMapLoaded({ isMapLoaded: true })
  }
  useEffect(handleAnimateMapToSelectedStore, [selectedStoreIndex])

  useEffect(getUserCurrentLocation, [])

  useEffect(() => {
    if (isMapLoaded) {
      Animated.timing(mapOverlayOpacityAnimation.current, {
        toValue: 0,
        duration: 1000,
        useNativeDriver: false,
      }).start()
    }
  }, [isMapLoaded])

  const [zoom, setZoom] = React.useState(15)
  const [userCoord, setUserCoords] = React.useState({
    lat: null,
    lng: null
  })
  const [center, setCenter] = React.useState({
    lat: 41.283105677319035,
    lng: 19.8568675032758
  })



  return (
    <View style={styles.container(theme)}>
      {(!isLoading && !storesError) ? (
        <div style={{ height: '100%', position: 'relative' }} >

          <Wrapper apiKey={'AIzaSyDgZm5OUSqiyK7fEstVVKhQCtQvkEvXjZo'} libraries={['places']}>
            <Map
              center={center}
              zoom={zoom}
              style={{ flexGrow: '1', height: '100%' }}
            >
              <Marker key={1} position={userCoord} isUserLocation={true} />
              {stores.map((latLng, i) => (
                <Marker key={i} position={{
                  lat: latLng.address.latitude,
                  lng: latLng.address.longitude
                }} />
              ))}
            </Map>
          </Wrapper>
          {/* <MapView
            ref={mapRef}
            initialRegion={initialRegion}
            showsUserLocation={false}
            style={styles.mapContainer}
            provider={PROVIDER_GOOGLE}
            customMapStyle={mapThemes}
            showsTraffic={false}
            showsCompass={false}
            showsBuildings={false}
            showsPointsOfInterest={false}
            onMapReady={onMapReady}
          >
            {userCurrentLocation && <Marker
              key={0}
              coordinate={userCurrentLocation}
              title={''}
            />}
            <StoresMarkers
              stores={stores}
              selectedStoreIndex={selectedStoreIndex}
              onStoreMarkerPress={storeIndex => {
                setSelectedStoreIndex(storeIndex)
              }}
            />
          </MapView> */}
          <CurrentLocationIcon onPress={handleNavigateToUserCurrentLocation} disabled={currentLocationDisabled} />
        </div>
      ) : null}
      {
        (!isLoading && storesError) ? <>
          <ErrorScreen
            text={storesError}
            onPress={getStoresData}
            backgroundStyle={styles.errorBackgroundStyle}
          />
        </> : <View style={styles.StoresCardsList(insets)}>
          <StoresCardsList
            stores={stores}
            isLoading={isLoading}
            selectedStoreIndex={selectedStoreIndex}
            onStoreChange={storeIndex => setSelectedStoreIndex(storeIndex)}
            onClick={storeIndex => setSelectedStoreIndex(storeIndex)}
            onShowTimeSlotsPress={onShowTimeSlotsPress}
            getStoresOpenHours={getStoresOpenHours}
          />
        </View>
      }
    </View>
  )
}
StoreStep.defaultProps = {
  initialSelectedStoreIndex: 0
}

StoreStep.propTypes = {
  stores: PropTypes.array,
  onShowTimeSlotsPress: PropTypes.func,
  isLoading: PropTypes.bool,
  initialSelectedStoreIndex: PropTypes.number,
  storesError: PropTypes.string,
  getStoresData: PropTypes.func,
  getStoresOpenHours: PropTypes.func,
}

export default StoreStep
